/**
 * Activity Timeline Component
 *
 * Unified timeline showing thesis changes, trades, and comments for a position.
 * Items are sorted by time descending (newest first).
 * Shows first 3 items by default with "Show more" to expand.
 */

import React, { useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  LayoutAnimation,
  Alert,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../../contexts/ThemeContext';
import { Card } from '../ui/Card';
import { usePositionTheses } from '../../services/api/queries/usePositionTheses';
import { usePositionTrades } from '../../services/api/queries/useTrades';
import { usePositionComments } from '../../services/api/queries/usePositionComments';
import { useCorporateActions } from '../../services/api/queries/useCorporateActions';
import type { PositionThesis } from '../../services/api/positionThesisApi';
import type { Trade } from '../../services/portfolioApi';
import type { PositionComment } from '../../services/api/positionCommentApi';
import type { SplitConsolidateResponse } from '../../services/tradesApi';
import { parseThesisText } from '../../types/thesis';
import {
  detectThesisChanges,
  formatVersionDate,
  truncateText,
} from '../../utils/thesisChangeDetection';
import { Ionicons } from '@expo/vector-icons';
import { showConfirm, showSuccess, showError } from '../../utils/alert';
import { deleteCorporateActionById } from '../../services/tradesApi';

interface ActivityTimelineProps {
  positionId: number;
  symbol: string;
  currency: string;
  onEditComment?: (comment: PositionComment) => void;
  onEditTrade?: (trade: Trade) => void;
  onEditCorporateAction?: (ca: SplitConsolidateResponse) => void;
  onDeleteCorporateAction?: (trade: Trade) => void;
}

type ActivityItem =
  | { type: 'trade'; data: Trade; timestamp: string }
  | {
      type: 'thesis';
      data: PositionThesis;
      timestamp: string;
      changes?: string[];
      hasTextChange?: boolean;
      thesisStatement?: string;
      thesisPillars?: string[];
      thesisRisks?: string[];
    }
  | { type: 'comment'; data: PositionComment; timestamp: string }
  | { type: 'corporateAction'; data: SplitConsolidateResponse; timestamp: string };

// Transaction type helper
const getTransactionTypeInfo = (trade: Trade, t: any) => {
  const txType = trade.transaction_type || 'buy';

  switch (txType) {
    case 'buy':
      return {
        label: t('portfolio.buy'),
        color: 'success',
        icon: 'trending-up',
      };
    case 'sell':
      return {
        label: t('portfolio.sell'),
        color: 'error',
        icon: 'trending-down',
      };
    case 'deposit':
      return {
        label: t('portfolio.deposit'),
        color: 'success',
        icon: 'add-circle',
      };
    case 'withdrawal':
      return {
        label: t('portfolio.withdrawal'),
        color: 'error',
        icon: 'remove-circle',
      };
    case 'split':
      return {
        label: t('portfolio.split'),
        color: 'primary',
        icon: 'call',
        isCorporateAction: true,
      };
    case 'consolidate':
      return {
        label: t('portfolio.consolidate'),
        color: 'primary',
        icon: 'call',
        isCorporateAction: true,
      };
    case 'bonus':
      return {
        label: t('portfolio.bonus'),
        color: 'primary',
        icon: 'gift',
        isCorporateAction: true,
      };
    default:
      return {
        label: txType,
        color: 'textSecondary',
        icon: 'swap-horizontal',
      };
  }
};

export function ActivityTimeline({
  positionId,
  symbol,
  currency,
  onEditComment,
  onEditTrade,
  onEditCorporateAction,
  onDeleteCorporateAction,
}: ActivityTimelineProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const queryClient = useQueryClient();

  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedItemIds, setExpandedItemIds] = useState<Set<string>>(new Set());

  const { data: thesisVersions = [], isLoading: thesisLoading } = usePositionTheses(positionId);
  const { data: trades = [], isLoading: tradesLoading } = usePositionTrades(symbol);
  const { data: comments = [], isLoading: commentsLoading } = usePositionComments(positionId);
  const { data: corporateActions = [], isLoading: caLoading } = useCorporateActions({ positionId });

  // Combine thesis changes, trades, corporate actions, and comments into unified timeline
  const activities = useMemo(() => {
    const items: ActivityItem[] = [];

    // Add thesis activities (skip the current/latest version, only show historical changes)
    for (let i = 1; i < thesisVersions.length; i++) {
      const version = thesisVersions[i];
      const nextVersion = thesisVersions[i - 1]; // The version after this one (chronologically newer)

      if (!nextVersion) continue;

      const changes = detectThesisChanges(version, nextVersion, t);
      if (!changes) continue;

      const changeSummaries = changes.map((c) => c.text);

      // Check if there's a thesis text change
      const hasTextChange = changes.some((c) => c.type === 'text');

      // Parse structured thesis (statement, pillars, risks)
      let thesisStatement: string | undefined;
      let thesisPillars: string[] | undefined;
      let thesisRisks: string[] | undefined;
      if (hasTextChange && nextVersion.thesis_text) {
        const parsed = parseThesisText(nextVersion.thesis_text);
        thesisStatement = parsed.statement || undefined;
        thesisPillars = parsed.pillars && parsed.pillars.length > 0 ? parsed.pillars : undefined;
        thesisRisks = parsed.risks && parsed.risks.length > 0 ? parsed.risks : undefined;
      }

      items.push({
        type: 'thesis',
        data: nextVersion,
        timestamp: version.updated_at,
        changes: changeSummaries,
        hasTextChange,
        thesisStatement,
        thesisPillars,
        thesisRisks,
      });
    }

    // Add trade activities
    trades.forEach((trade) => {
      items.push({
        type: 'trade',
        data: trade,
        timestamp: trade.trade_datetime,
      });
    });

    // Add corporate action activities (use exclude_date as the effective date)
    corporateActions.forEach((ca) => {
      items.push({
        type: 'corporateAction',
        data: ca,
        timestamp: ca.exclude_date,
      });
    });

    // Add comment activities
    comments.forEach((comment) => {
      items.push({
        type: 'comment',
        data: comment,
        timestamp: comment.created_at,
      });
    });

    // Sort by timestamp descending (newest first)
    items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    return items;
  }, [thesisVersions, trades, corporateActions, comments, t]);

  const toggleExpanded = () => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    }
    setIsExpanded(!isExpanded);
  };

  const toggleItemExpansion = (itemId: string) => {
    const newExpanded = new Set(expandedItemIds);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItemIds(newExpanded);
  };

  const handleEditComment = (comment: PositionComment) => {
    if (onEditComment) {
      onEditComment(comment);
    }
  };

  const handleDeleteCorporateAction = async (item: Trade | SplitConsolidateResponse) => {
    if (!onDeleteCorporateAction) {
      return;
    }

    const confirmed = await showConfirm(
      t('portfolio.confirmDeleteCorporateAction', { defaultValue: 'Delete Corporate Action?' }),
      t('portfolio.caDeleteWarning', {
        defaultValue:
          'This will reverse the effects of this corporate action and restore position quantity, average cost, and trade prices. Continue?',
      }),
      {
        confirmText: t('common.delete', { defaultValue: 'Delete' }),
        cancelText: t('common.cancel', { defaultValue: 'Cancel' }),
        destructiveAction: true,
      }
    );

    if (!confirmed) return;

    try {
      // Check if this is a corporate action item (has action_type property)
      if ('action_type' in item) {
        await deleteCorporateActionById(item.id);
        // Invalidate corporate actions cache to refresh the timeline
        queryClient.invalidateQueries({ queryKey: ['corporateActions', positionId] });
        // Also invalidate trades and positions since they may be affected
        queryClient.invalidateQueries({ queryKey: ['trades'] });
        queryClient.invalidateQueries({ queryKey: ['positions'] });
      } else {
        await onDeleteCorporateAction(item as Trade);
      }
      showSuccess(
        t('portfolio.caDeleteSuccess', { defaultValue: 'Corporate action deleted successfully' })
      );
    } catch (error: any) {
      showError(
        error.message ||
          t('portfolio.caDeleteFailed', { defaultValue: 'Failed to delete corporate action' })
      );
    }
  };

  // Show only first 3 items if not expanded
  const displayedActivities = isExpanded ? activities : activities.slice(0, 3);
  const hasMoreItems = activities.length > 3;

  if (thesisLoading || tradesLoading || commentsLoading || caLoading) {
    return (
      <Card style={styles.container}>
        <Text style={[styles.header, { color: theme.colors.text }]}>
          {t('portfolio.positionDetails.activityTimeline', { defaultValue: 'Activity Timeline' })}
        </Text>
        <Text style={[styles.loadingText, { color: theme.colors.textSecondary }]}>
          {t('common.loading', { defaultValue: 'Loading...' })}
        </Text>
      </Card>
    );
  }

  if (activities.length === 0) {
    return (
      <Card style={styles.container}>
        <Text style={[styles.header, { color: theme.colors.text }]}>
          {t('portfolio.positionDetails.activityTimeline', { defaultValue: 'Activity Timeline' })}
        </Text>
        <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
          {t('portfolio.positionDetails.noActivity', { defaultValue: 'No activity yet' })}
        </Text>
      </Card>
    );
  }

  return (
    <Card style={styles.container}>
      <Text style={[styles.header, { color: theme.colors.text }]}>
        {t('portfolio.positionDetails.activityTimeline', { defaultValue: 'Activity Timeline' })}
      </Text>

      <View style={styles.timeline}>
        {displayedActivities.map((item) => {
          const itemId = `${item.type}-${item.data.id}`;
          const isItemExpanded = expandedItemIds.has(itemId);

          // Trade item
          if (item.type === 'trade') {
            const txInfo = getTransactionTypeInfo(item.data, t);
            const txType = item.data.transaction_type || 'buy';
            const quantity = parseFloat(item.data.quantity);
            const absoluteQuantity = Math.abs(quantity);
            const isCorporateAction = txInfo.isCorporateAction;

            return (
              <View key={itemId} style={styles.timelineItem}>
                <View style={styles.timelineIcon}>
                  <View
                    style={[
                      styles.iconContainer,
                      {
                        backgroundColor: theme.colors[txInfo.color] || theme.colors.primary,
                        ...(isCorporateAction ? styles.corporateActionIcon : {}),
                      },
                    ]}
                  >
                    <Ionicons name={txInfo.icon as any} size={16} color={theme.colors.background} />
                  </View>
                </View>
                <View style={styles.timelineContent}>
                  <View style={styles.tradeHeader}>
                    <Text style={[styles.itemDate, { color: theme.colors.textSecondary }]}>
                      {formatVersionDate(item.timestamp)}
                    </Text>
                    <View style={styles.tradeHeaderActions}>
                      <View
                        style={[
                          styles.transactionTypeBadge,
                          {
                            backgroundColor:
                              theme.colors[txInfo.color] + '20' || theme.colors.backgroundElevated,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.transactionTypeText,
                            { color: theme.colors[txInfo.color] || theme.colors.textSecondary },
                          ]}
                        >
                          {txInfo.label}
                        </Text>
                      </View>
                      {isCorporateAction && onDeleteCorporateAction ? (
                        <TouchableOpacity
                          onPress={() => handleDeleteCorporateAction(item.data)}
                          style={styles.editButton}
                          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                        >
                          <Ionicons name="trash-outline" size={16} color={theme.colors.error} />
                        </TouchableOpacity>
                      ) : onEditTrade && !isCorporateAction ? (
                        <TouchableOpacity
                          onPress={() => onEditTrade(item.data)}
                          style={styles.editButton}
                          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                        >
                          <Ionicons name="create-outline" size={16} color={theme.colors.primary} />
                        </TouchableOpacity>
                      ) : null}
                    </View>
                  </View>

                  {/* Corporate action display - show description instead of bought/sold */}
                  {isCorporateAction ? (
                    <>
                      <Text style={[styles.itemTitle, { color: theme.colors.text }]}>
                        {item.data.symbol} {txInfo.label}
                      </Text>
                      {item.data.code && (
                        <Text style={[styles.caDetailsText, { color: theme.colors.textSecondary }]}>
                          {item.data.code}
                        </Text>
                      )}
                      {item.data.comment && (
                        <Text
                          style={[styles.commentText, { color: theme.colors.textTertiary }]}
                          numberOfLines={3}
                        >
                          "{item.data.comment}"
                        </Text>
                      )}
                    </>
                  ) : txType === 'deposit' || txType === 'withdrawal' ? (
                    <>
                      <Text style={[styles.itemTitle, { color: theme.colors.text }]}>
                        {txInfo.label} {absoluteQuantity} {item.data.symbol}
                      </Text>
                      {item.data.comment && (
                        <Text
                          style={[styles.commentText, { color: theme.colors.textTertiary }]}
                          numberOfLines={2}
                        >
                          "{item.data.comment}"
                        </Text>
                      )}
                    </>
                  ) : (
                    <>
                      <Text style={[styles.itemTitle, { color: theme.colors.text }]}>
                        {txType === 'buy'
                          ? t('portfolio.positionDetails.bought', {
                              defaultValue: '{{quantity}} {{symbol}} @ {{price}}',
                              quantity: absoluteQuantity,
                              symbol: item.data.symbol,
                              price: parseFloat(item.data.trade_price).toFixed(2),
                            })
                          : t('portfolio.positionDetails.sold', {
                              defaultValue: '{{quantity}} {{symbol}} @ {{price}}',
                              quantity: absoluteQuantity,
                              symbol: item.data.symbol,
                              price: parseFloat(item.data.trade_price).toFixed(2),
                            })}
                      </Text>
                      {item.data.comment && (
                        <Text
                          style={[styles.commentText, { color: theme.colors.textTertiary }]}
                          numberOfLines={2}
                        >
                          "{item.data.comment}"
                        </Text>
                      )}
                    </>
                  )}

                  {!isCorporateAction &&
                    item.data.realized_pl &&
                    parseFloat(item.data.realized_pl) !== 0 && (
                      <Text
                        style={[
                          styles.itemPL,
                          {
                            color:
                              parseFloat(item.data.realized_pl) >= 0
                                ? theme.colors.success
                                : theme.colors.error,
                          },
                        ]}
                      >
                        {t('portfolio.realizedPL', { defaultValue: 'Realized P/L' })}:{' '}
                        {parseFloat(item.data.realized_pl) >= 0 ? '+' : ''}
                        {currency} {parseFloat(item.data.realized_pl).toFixed(2)}
                      </Text>
                    )}
                </View>
              </View>
            );
          }

          // Comment item
          if (item.type === 'comment') {
            return (
              <View key={itemId} style={styles.timelineItem}>
                <View style={styles.timelineIcon}>
                  <View style={[styles.iconContainer, { backgroundColor: theme.colors.secondary }]}>
                    <Ionicons
                      name="chatbubble-ellipses"
                      size={16}
                      color={theme.colors.textInverse}
                    />
                  </View>
                </View>
                <View style={styles.timelineContent}>
                  <View style={styles.commentHeader}>
                    <Text style={[styles.itemDate, { color: theme.colors.textSecondary }]}>
                      {formatVersionDate(item.timestamp)}
                    </Text>
                    {onEditComment && (
                      <TouchableOpacity
                        onPress={() => handleEditComment(item.data)}
                        style={styles.editButton}
                        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                      >
                        <Ionicons name="create-outline" size={16} color={theme.colors.primary} />
                      </TouchableOpacity>
                    )}
                  </View>
                  <Text style={[styles.commentText, { color: theme.colors.text }]}>
                    {item.data.comment_text}
                  </Text>
                  {item.data.next_revisit_date && (
                    <View style={styles.revisitTag}>
                      <Ionicons name="calendar" size={12} color={theme.colors.primary} />
                      <Text style={[styles.revisitTagText, { color: theme.colors.primary }]}>
                        {t('portfolio.positionComment.revisitOn', {
                          defaultValue: 'Revisit: {{date}}',
                          date: item.data.next_revisit_date,
                        })}
                      </Text>
                    </View>
                  )}
                </View>
              </View>
            );
          }

          // Corporate action item
          if (item.type === 'corporateAction') {
            const caData = item.data;
            const txInfo = getTransactionTypeInfo(
              { transaction_type: caData.action_type } as Trade,
              t
            );

            return (
              <View key={itemId} style={styles.timelineItem}>
                <View style={styles.timelineIcon}>
                  <View
                    style={[
                      styles.iconContainer,
                      {
                        backgroundColor: theme.colors[txInfo.color] || theme.colors.primary,
                        ...(txInfo.isCorporateAction ? styles.corporateActionIcon : {}),
                      },
                    ]}
                  >
                    <Ionicons name={txInfo.icon as any} size={16} color={theme.colors.background} />
                  </View>
                </View>
                <View style={styles.timelineContent}>
                  <View style={styles.tradeHeader}>
                    <Text style={[styles.itemDate, { color: theme.colors.textSecondary }]}>
                      {formatVersionDate(item.timestamp)}
                    </Text>
                    <View style={styles.tradeHeaderActions}>
                      <View
                        style={[
                          styles.transactionTypeBadge,
                          {
                            backgroundColor:
                              theme.colors[txInfo.color] + '20' || theme.colors.backgroundElevated,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.transactionTypeText,
                            { color: theme.colors[txInfo.color] || theme.colors.textSecondary },
                          ]}
                        >
                          {txInfo.label}
                        </Text>
                      </View>
                      {onEditCorporateAction ? (
                        <TouchableOpacity
                          onPress={() => onEditCorporateAction(item.data)}
                          style={styles.editButton}
                          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                        >
                          <Ionicons name="create-outline" size={16} color={theme.colors.primary} />
                        </TouchableOpacity>
                      ) : null}
                    </View>
                  </View>

                  <Text style={[styles.itemTitle, { color: theme.colors.text }]}>
                    {caData.symbol} {txInfo.label}
                  </Text>

                  {caData.description && (
                    <Text style={[styles.caDetailsText, { color: theme.colors.textSecondary }]}>
                      {caData.description}
                    </Text>
                  )}

                  {/* Show ratio details */}
                  <Text style={[styles.commentText, { color: theme.colors.textTertiary }]}>
                    {t('portfolio.positionDetails.fromTo', {
                      defaultValue: '{{from}} for {{to}}',
                      from: caData.from_shares,
                      to: caData.to_shares,
                    })}
                  </Text>
                </View>
              </View>
            );
          }

          // Thesis item
          const hasThesisContent = item.thesisStatement || item.thesisPillars || item.thesisRisks;
          const showExpandable = hasThesisContent && (item.thesisPillars || item.thesisRisks);

          return (
            <TouchableOpacity
              key={itemId}
              onPress={() => showExpandable && toggleItemExpansion(itemId)}
              activeOpacity={showExpandable ? 0.7 : 1}
              style={styles.timelineItem}
            >
              <View style={styles.timelineIcon}>
                <View style={[styles.iconContainer, { backgroundColor: theme.colors.border }]}>
                  <Ionicons name="document-text" size={16} color={theme.colors.textSecondary} />
                </View>
              </View>
              <View style={styles.timelineContent}>
                <Text style={[styles.itemDate, { color: theme.colors.textSecondary }]}>
                  {formatVersionDate(item.timestamp)}
                </Text>

                {/* Statement */}
                {item.thesisStatement && (
                  <Text style={[styles.thesisStatement, { color: theme.colors.text }]}>
                    {item.thesisStatement}
                  </Text>
                )}

                {/* Pillars */}
                {item.thesisPillars && item.thesisPillars.length > 0 && (
                  <View style={styles.thesisSection}>
                    <Text
                      style={[styles.thesisSectionLabel, { color: theme.colors.textSecondary }]}
                    >
                      {t('portfolio.keyPillars', { defaultValue: 'Key Pillars' })}
                    </Text>
                    {item.thesisPillars.map((pillar, idx) => (
                      <View key={idx} style={styles.thesisBulletItem}>
                        <Text style={[styles.thesisBullet, { color: theme.colors.secondary }]}>
                          •
                        </Text>
                        <Text style={[styles.thesisBulletText, { color: theme.colors.text }]}>
                          {pillar}
                        </Text>
                      </View>
                    ))}
                  </View>
                )}

                {/* Risks */}
                {item.thesisRisks && item.thesisRisks.length > 0 && (
                  <View style={styles.thesisSection}>
                    <Text
                      style={[styles.thesisSectionLabel, { color: theme.colors.textSecondary }]}
                    >
                      {t('portfolio.keyRisks', { defaultValue: 'Key Risks' })}
                    </Text>
                    {item.thesisRisks.map((risk, idx) => (
                      <View key={idx} style={styles.thesisBulletItem}>
                        <Text style={[styles.thesisBullet, { color: theme.colors.error }]}>•</Text>
                        <Text style={[styles.thesisBulletText, { color: theme.colors.text }]}>
                          {risk}
                        </Text>
                      </View>
                    ))}
                  </View>
                )}

                {/* Other changes (conviction, duration, prices) */}
                {item.changes && item.changes.length > 0 && (
                  <View style={styles.changesContainer}>
                    {item.changes.map((change, idx) => (
                      <Text
                        key={idx}
                        style={[styles.changeText, { color: theme.colors.textSecondary }]}
                      >
                        {idx > 0 && ' • '}
                        {change}
                      </Text>
                    ))}
                  </View>
                )}
              </View>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Show more/less button for timeline */}
      {hasMoreItems && (
        <TouchableOpacity
          onPress={toggleExpanded}
          style={[styles.showMoreContainer, { borderTopColor: theme.colors.border }]}
        >
          <Text style={[styles.showMoreText, { color: theme.colors.primary }]}>
            {isExpanded
              ? t('portfolio.positionDetails.showLess', { defaultValue: 'Show less' })
              : t('common.showMore', {
                  defaultValue: 'Show {{count}} more',
                  count: activities.length - 3,
                })}
          </Text>
        </TouchableOpacity>
      )}
    </Card>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      marginHorizontal: 12,
      marginTop: 8,
      marginBottom: 12,
      padding: 12,
    },
    header: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: 12,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    loadingText: {
      fontSize: theme.typography.fontSizeSm,
      fontStyle: 'italic',
    },
    emptyText: {
      fontSize: theme.typography.fontSizeSm,
      fontStyle: 'italic',
      textAlign: 'center',
      paddingVertical: 16,
    },
    timeline: {
      position: 'relative',
    },
    timelineItem: {
      flexDirection: 'row',
      paddingVertical: 10,
    },
    tradeHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 4,
    },
    tradeHeaderActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    transactionTypeBadge: {
      paddingHorizontal: 6,
      paddingVertical: 2,
      borderRadius: 4,
    },
    transactionTypeText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: 'uppercase',
    },
    corporateActionIcon: {
      borderWidth: 2,
      borderColor: theme.colors.primary,
    },
    timelineIcon: {
      alignItems: 'flex-start',
      paddingTop: 2,
      marginRight: 12,
    },
    iconContainer: {
      width: 28,
      height: 28,
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
    },
    timelineContent: {
      flex: 1,
      paddingBottom: 10,
    },
    itemTitle: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightMedium,
      marginTop: 2,
    },
    thesisFirstLine: {
      fontSize: theme.typography.fontSizeSm,
      marginTop: 2,
      lineHeight: 18,
    },
    thesisStatement: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightMedium,
      marginTop: 2,
      lineHeight: 18,
    },
    thesisSection: {
      marginTop: theme.spacing.sm || 8,
    },
    thesisSectionLabel: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
      marginTop: theme.spacing.xs || 4,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? -0.3 : 0,
    },
    thesisBulletItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginTop: 2,
    },
    thesisBullet: {
      marginRight: theme.spacing.xs || 4,
      fontSize: theme.typography.fontSizeSm,
    },
    thesisBulletText: {
      flex: 1,
      fontSize: theme.typography.fontSizeSm,
      lineHeight: 16,
    },
    commentHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 2,
    },
    editButton: {
      padding: 4,
      marginLeft: 8,
    },
    commentText: {
      fontSize: theme.typography.fontSizeSm,
      marginTop: 2,
      lineHeight: 18,
    },
    caDetailsText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginTop: 2,
      color: theme.colors.textSecondary,
    },
    changesContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginTop: 4,
    },
    changeText: {
      fontSize: theme.typography.fontSizeXs,
      lineHeight: 16,
    },
    itemPL: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginTop: 2,
    },
    itemDate: {
      fontSize: theme.typography.fontSizeXs,
    },
    revisitTag: {
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: 4,
      gap: 4,
    },
    revisitTagText: {
      fontSize: theme.typography.fontSizeXs,
    },
    showMoreLink: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
      marginTop: 4,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    showMoreContainer: {
      paddingTop: 12,
      marginTop: 8,
      borderTopWidth: theme.id === 'bauhaus' ? 2 : 1,
      alignItems: 'center',
    },
    showMoreText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
  });
}
